import React, { useMemo, useState } from "react";
import { useInitiateEmailChange } from "../../hooks/user";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, InputAdornment, Alert, Snackbar, useTheme, useMediaQuery, Typography } from "@mui/material";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon, Email as EmailIcon, Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { ModernTextField, GradientButton } from "../common/StyledComponents";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { VERIFY_EMAIL_CHANGE_ROUTE_PATH } from "../../utils/constants/AppConstants";

type ChangeEmailDialogProps = {
  open: boolean;
  onClose: () => void;
  onInitiated?: () => void;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ChangeEmailDialog: React.FC<ChangeEmailDialogProps> = ({ open, onClose, onInitiated }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { error, helpText, isLoading, clearError, handleError } = useError();

  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successOpen, setSuccessOpen] = useState(false);

  const isEmailValid = useMemo(() => emailRegex.test(newEmail), [newEmail]);

  const setFieldError = (field: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };
  const clearAllFieldErrors = () => setFieldErrors({});

  const buildVerificationCallbackUrl = (): string => {
    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${VERIFY_EMAIL_CHANGE_ROUTE_PATH}`;
    return new URL(path, window.location.origin).toString();
  };

  const validate = (): boolean => {
    clearAllFieldErrors();
    let valid = true;
    if (!newEmail.trim() || !isEmailValid) {
      setFieldError("newEmail", "Enter a valid email like name@example.com.");
      valid = false;
    }
    if (!currentPassword.trim()) {
      setFieldError("currentPassword", "Current password is required.");
      valid = false;
    }
    return valid;
  };

  const { mutate: initiateEmailChange } = useInitiateEmailChange();

  const onSubmit = () => {
    clearError();
    if (!validate()) return;
    const callback = buildVerificationCallbackUrl();
    
    initiateEmailChange(
      { newEmail: newEmail.trim(), currentPassword, verificationCallbackUrl: callback },
      {
        onSuccess: () => {
          setSuccessOpen(true);
          setNewEmail("");
          setCurrentPassword("");
          onClose();
          if (onInitiated) onInitiated();
        },
        onError: (error) => {
          // Use the backend's user-friendly error message
          handleError(error);
        }
      }
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle sx={{ fontWeight: 700 }}>Change email</DialogTitle>
        <DialogContent dividers>
          <ErrorAlert error={error} helpText={helpText} onClear={clearError} title="Unable to change email" />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We’ll send verification links to your current and new email. The change completes only after both are verified.
          </Typography>

          <ModernTextField
            fullWidth
            margin="normal"
            label="New email address"
            type="email"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              clearFieldError("newEmail");
            }}
            error={!!fieldErrors.newEmail}
            helperText={fieldErrors.newEmail || "Enter the email you want to use for your account"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              )}}
          />

          <ModernTextField
            fullWidth
            margin="normal"
            label="Current password"
            type={showPassword ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              clearFieldError("currentPassword");
            }}
            error={!!fieldErrors.currentPassword}
            helperText={fieldErrors.currentPassword || "We require your password to confirm this change"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword((s) => !s)} edge="end">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )}}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} startIcon={<CancelIcon />} disabled={isLoading}>Cancel</Button>
          <GradientButton
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSubmit}
            disabled={isLoading || !isEmailValid || !currentPassword}
          >
            {isLoading ? "Submitting..." : "Send verification emails"}
          </GradientButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={successOpen} autoHideDuration={4000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Email change initiated. Please check both email inboxes.
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangeEmailDialog;



